import { IHttpClient } from "./HttpClient";

/**
 * A base class for JSON services
 */
export class JsonService {
  /**
   * The header that is used for anti-forgery validation
   */
  private static AntiForgeryHeader = "X-RequestVerificationToken";

  /**
   * The header that is used for authorization
   */
  private static AuthorizationHeader = "Authorization";

  /**
   * The API version to send with requests
   */
  public apiVersion: string;

  /**
   * The client for making HTTP calls
   */
  private httpClient: IHttpClient;

  /**
   * Initializes a new instance of the SettingsService class.
   *
   * @param apiVersion - The API version
   * @param httpClient - the optional HTTP client
   */
  public constructor(apiVersion: string, httpClient: IHttpClient) {
    this.apiVersion = apiVersion;
    this.httpClient = httpClient;
  }

  /**
   * Gets JSON data from a service
   *
   * @param url - The service URL
   * @param cache - Whether we allow cached responses
   * @returns - the response
   */
  protected getJsonAsync<TResponse>(url: string, cache = true): Promise<TResponse> {
    return this.httpClient.getJsonAsync({
      url,
      cache,
    });
  }

  /**
   * Posts JSON data to a service and receives a response
   *
   * @param url - the service URL
   * @param antiForgeryToken - The anti-forgery token to protect against CSRF attacks
   * @param request - The request JSON data
   * @param bearerToken - The OAuth bearer token for authorization
   * @returns - the response
   */
  protected postJsonAsync<TRequest, TResponse>(
    url: string,
    antiForgeryToken: string,
    request: TRequest,
    bearerToken?: string
  ): Promise<TResponse> {
    return this.httpClient.postJsonAsync({
      url,
      headers: {
        [JsonService.AntiForgeryHeader]: antiForgeryToken,
        ...(bearerToken ? { [JsonService.AuthorizationHeader]: `Bearer ${bearerToken}` } : {}),
      },
      data: request,
    });
  }
}
